import {
    Features,
    useFeatureFlagValue,
} from '@experiences/feature-flags';
import {
    UiAlertBanner,
    UiText,
} from '@experiences/ui-common';
import { useRouteResolver } from '@experiences/util';
import { ApLink } from '@uipath/portal-shell-react';
import React, {
    useEffect,
    useMemo,
} from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import useSWR from 'swr';

import {
    MigratingCloudOrganizationDataLink,
    MigratingCloudTenantDataLink,
} from '../../../common/constants/documentation/DocumentationLinksDefault';
import { OrganizationSettingsAdvanced } from '../../../common/constants/RouteNames';
import { useDocumentationLinks } from '../../../common/hooks/useDocumentationLink';
import useIsOnPrem from '../../../common/hooks/useIsOnPrem';
import { getOrganizationScheduledMigrationStats } from '../../../services/global-workflow-service/OrgMigration';
import { accountGlobalId } from '../../../store/selectors';

export const UiUpcomingMigrationBanner: React.FC = () => {
    const getLocalizedLinks = useDocumentationLinks();
    const { formatMessage: translate } = useIntl();
    const organizationId = useSelector(accountGlobalId);
    const isMigrationBannerEnabled = useFeatureFlagValue(Features.EnableMigrationBanner.name);
    const getRoute = useRouteResolver();

    const [ bannerMessage, setBannerMessage ] = React.useState<string>('CLIENT_ORG_MIGRATION_BANNER_INFO');
    const [ docsLink, setDocsLink ] = React.useState<string>(MigratingCloudOrganizationDataLink);

    const isOnPrem = useIsOnPrem();

    const {
        data, isLoading,
    } = useSWR('organizationScheduledMigrationStats', () => getOrganizationScheduledMigrationStats(organizationId));

    const isMigrationScheduled = useMemo(
        () => !isLoading && data && (data.hasOrganizationMigration || data.scheduledTenantIds.length > 0),
        [ data, isLoading ]
    );

    useEffect(() => {
        if (!data) {
            return;
        }
        if (data.hasOrganizationMigration && data.scheduledTenantIds.length === 0) {
            setBannerMessage('CLIENT_ORG_MIGRATION_BANNER_INFO');
            setDocsLink(MigratingCloudOrganizationDataLink);
        } else if (data.hasOrganizationMigration && data.scheduledTenantIds.length > 0) {
            setBannerMessage('CLIENT_ORG_AND_TENANT_MIGRATION_BANNER_INFO');
            setDocsLink(MigratingCloudOrganizationDataLink);
        } else if (data.scheduledTenantIds.length > 0) {
            setBannerMessage('CLIENT_TENANT_MIGRATION_BANNER_INFO');
            setDocsLink(MigratingCloudTenantDataLink);
        }

    }, [ data ]);

    const infoMigrationMessage = useMemo(() => (
        <UiText
            data-cy='migration-banner-info'>
            {translate(
                { id: bannerMessage },
                {
                    settingsLink: (
                        <ApLink
                            href={getRoute(OrganizationSettingsAdvanced)}
                        >
                            <UiText style={{ display: 'inline' }}>
                                {translate({ id: 'CLIENT_ORG_DATA_MIGRATION_SETTINGS' })}
                            </UiText>
                        </ApLink>
                    ),
                    docsLink: (
                        <ApLink
                            target="_blank"
                            href={getLocalizedLinks({ articleSlug: docsLink })}>
                            <UiText style={{ display: 'inline' }}>
                                {translate({ id: 'CLIENT_DATA_MIGRATION_DOCS' })}
                            </UiText>
                        </ApLink>
                    ),
                },
            )}
        </UiText>
    ), [ bannerMessage, docsLink, getLocalizedLinks, translate, getRoute ]);

    return (isMigrationBannerEnabled && !isOnPrem && isMigrationScheduled && (<UiAlertBanner
        type="info"
        closeable={false}
        dataCy="migration-banner"
    >
        {infoMigrationMessage}
    </UiAlertBanner>));

};
